import AlertPreferencesEmailModal from "@/components/core/modals/AlertPreferencesEmailModal";
import {
  requestGetAlertRecapPreferences,
  requestUpdateAlertRecapPreferences,
} from "@/state/alertRecapPreferences/AlertRecapPreferencesEffects";
import {
  requestGetAlertPreferences,
  requestUpdateAlertPreferences,
} from "@/state/alertsPreferences/AlertPreferencesEffects";
import { MailRecipientDTO } from "@/state/alertsPreferences/dto/response/mail.recipient.dto";
import { PreferenceDTO } from "@/state/alertsPreferences/dto/response/preference.dto";
import { ALERT_OBJECT_ENUM, DISPLAYED_PREFERENCES } from "@/utils/Constant";
import { AlertRecapBundleByEnum } from "@/utils/enums/alert.recap.bundle.by.enum";
import { toastError, toastSuccess } from "@/utils/helpers/toast-helper";
import Accordions from "@components/core/accordions/Accordions";
import PageLayout from "@components/core/layouts/PageLayout";
import Section from "@components/core/section/Section";
import { ROUTES } from "@routes/Routes";
import MyPreferencesAlert from "@views/core/extranet/menu/account/myPreferences/sections/MyPreferencesAlert";
import { Col, Row } from "antd";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AlertPeriodicity from "@views/core/extranet/menu/account/myPreferences/AlertPeriodicity";
import AlertPreferences from "@views/core/extranet/menu/account/myPreferences/AlertPreferences";

const MyPreferencesScreen: FunctionComponent = () => {
  const { t } = useTranslation();

  const recapPrefsRef = useRef(null);

  const [mailRecipients, setMailRecipients] = useState<MailRecipientDTO[]>([]);
  const [
    isAlertPreferencesEmailModalVisible,
    setIsAlertPreferencesEmailModalVisible,
  ] = useState<boolean>(false);
  const [bundleBy, setBundleBy] = useState<AlertRecapBundleByEnum>();
  const [preferences, setPreferences] = useState<PreferenceDTO[]>();
  const [noPeriodicitySelected, setNoPeriodicitySelected] =
    useState<boolean>(false);

  /* API calls */
  useEffect(() => {
    void requestGetAlertPreferences({});
    void requestGetAlertRecapPreferences({});
  }, []);

  /* API manage response */
  useEffect(() => {
    return requestGetAlertPreferences.done.watch(({ result }) => {
      if (result.ok && result.data?.preferences) {
        setPreferences(result.data.preferences);
      } else {
        toastError(t("error.user.getAlertPreferences"));
      }
    });
  }, [t]);

  useEffect(() => {
    return requestGetAlertRecapPreferences.done.watch(({ result }) => {
      if (result.ok) {
        if (result.data?.to) {
          setMailRecipients(result.data.to);
        }
        if (result.data?.bundleBy) {
          setBundleBy(result.data.bundleBy);
        }
      } else {
        toastError(t("error.user.getAlertRecapPreferences"));
      }
    });
  }, [t]);

  const handleSaveAlertPreferences = (
    preference: PreferenceDTO,
    callback?: () => void,
  ) => {
    requestUpdateAlertPreferences({ dto: preference })
      .then((response) => {
        if (response.ok && response.data) {
          setPreferences((previousState) => {
            return previousState?.map((value) => {
              if (value.type === response.data?.type) {
                return response.data;
              } else {
                return value;
              }
            });
          });
        } else {
          toastError(t("error.toast.recapPreferences.updateAlert"));
        }
        callback?.();
      })
      .catch(() => {
        toastError(t("error.toast.recapPreferences.updateAlert"));
      });
  };

  const handleSaveAlertRecapPreferences = (
    newMailsRecipients: MailRecipientDTO[],
  ) => {
    requestUpdateAlertRecapPreferences({
      dto: { to: newMailsRecipients },
    })
      .then(() => {
        toastSuccess(t("success.recapPreferences.sendingMail"));
        setMailRecipients(newMailsRecipients);
      })
      .catch(() => {
        toastError(t("error.toast.recapPreferences.sendingMail"));
      });
  };

  return (
    <PageLayout container returnRoute={ROUTES.extranet.home.generate()}>
      <>
        <Section>
          <Accordions
            defaultActiveKey={["recapEmail"]}
            sections={[
              {
                key: "recapEmail",
                title: t("account.myPreferences.recapEmail"),
                content: (
                  <>
                    <div ref={recapPrefsRef} className="p-8">
                      <Row gutter={16} className="mb-48">
                        <Col span={24} sm={2} md={4} xl={6} />
                        <Col span={24} sm={8} md={6} xl={4}>
                          <label>
                            {`${t("account.myPreferences.recap.sendRecap")} ${t(
                              "account.myPreferences.recap.to",
                            )}`}
                          </label>
                        </Col>
                        <Col span={24} sm={14}>
                          <ul className="mt-0">
                            {mailRecipients.map(
                              (mailRecipient) =>
                                mailRecipient.email &&
                                mailRecipient.email.length > 0 && (
                                  <li key={`recap_${mailRecipient.email}`}>
                                    {`${t("account.myPreferences.recipientType." + mailRecipient.recipientType)} : ${mailRecipient.email}`}
                                  </li>
                                ),
                            )}
                            <a
                              onClick={() =>
                                setIsAlertPreferencesEmailModalVisible(true)
                              }
                            >
                              {t(
                                "account.myPreferences.recap.customizeAddress",
                              )}
                            </a>
                          </ul>
                        </Col>
                      </Row>
                      <AlertPeriodicity
                        setAlertPeriodicityIsNever={setNoPeriodicitySelected}
                      />
                      <AlertPreferences
                        bundleBy={bundleBy}
                        setBundleBy={setBundleBy}
                      />
                    </div>
                  </>
                ),
              },
              {
                key: "preferencesByType",
                title: t("account.myPreferences.preferencesByType"),
                content: (
                  <>
                    <div className="p-8">
                      {preferences
                        ?.sort((a, b) => {
                          if (a.type && b.type) {
                            const indexA = DISPLAYED_PREFERENCES.indexOf(
                              a.type,
                            );
                            const indexB = DISPLAYED_PREFERENCES.indexOf(
                              b.type,
                            );
                            return indexA - indexB;
                          }
                          return 0;
                        })
                        .filter(
                          (preference) =>
                            preference.type &&
                            DISPLAYED_PREFERENCES.includes(preference.type),
                        )
                        ?.map((preference, index) => {
                          const alert = preference.type
                            ? ALERT_OBJECT_ENUM[preference.type]
                            : undefined;
                          return (
                            <MyPreferencesAlert
                              index={index}
                              key={`alert_${preference.type}`}
                              recapPrefsRef={recapPrefsRef}
                              preference={preference}
                              onSaveAlertPreferences={
                                handleSaveAlertPreferences
                              }
                              instantEmailPref={alert?.instantEmailPref}
                              delayType={alert?.delayType}
                              notificationClassicConfiguration={
                                alert?.notificationClassicConfiguration
                              }
                              noPeriodicitySelected={noPeriodicitySelected}
                              isMultipleThreshold={alert?.multipleThreshold}
                            />
                          );
                        })}
                    </div>
                  </>
                ),
              },
            ]}
          />
        </Section>
        {isAlertPreferencesEmailModalVisible && (
          <AlertPreferencesEmailModal
            mailRecipients={mailRecipients}
            visible={isAlertPreferencesEmailModalVisible}
            onCancel={() => setIsAlertPreferencesEmailModalVisible(false)}
            onSave={handleSaveAlertRecapPreferences}
          />
        )}
      </>
    </PageLayout>
  );
};

export default MyPreferencesScreen;
